<template>
  <!-- The list scrolls inside the view, not the page - #nav is a fixed 50px bar, and
       nothing above gives this view a resolvable height, so state it here. -->
  <div class="flex h-[calc(100vh-50px)] flex-col overflow-hidden">
    <!-- Header & Filters -->
    <div class="sticky top-0 z-20 flex items-center gap-3  bg-[#13191d] px-3.5 py-2">
      <h2 class="mr-auto flex min-w-0 shrink items-center gap-1.5 whitespace-nowrap text-xs font-medium uppercase tracking-[0.6px] text-[#a9c2e8]">
        Ops Monitoring
        <span class="text-[#82909a]">({{ currentCount }})</span>
        <ma-loading v-if="busy" class="bly-loading animate-spin text-xs text-[#ff5f5f]"></ma-loading>
      </h2>

      <div class="flex min-w-0 shrink items-center gap-2 max-[1100px]:hidden">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search events, inputs, outputs..."
          class="h-[26px] min-w-[90px] shrink basis-[220px] rounded-xs border border-[#2e3b43] bg-[#13191d] px-2 text-[11px]! text-[#cbd5da]! placeholder:text-[#5d6a72] focus:border-[#3d4c55] focus:outline-none"
        />

        <div class="flex shrink-0 *:rounded-none first:*:rounded-l-xs last:*:rounded-r-xs">
          <button
            class="inline-flex h-[26px] shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap border border-[#2e3b43] px-2.5 text-[11px]! font-medium leading-none! transition-colors"
            :class="showLive ? 'border-[#3fd23f] bg-[#3fd23f] text-[#10240f]!' : 'bg-transparent text-[#cbd5da]! hover:bg-[#212a2f] hover:text-[#e9eef1]!'"
            @click="showLive = !showLive"
          >
            Live ({{ phaseCounts.live }})
          </button>
          <button
            class="-ml-px inline-flex h-[26px] shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap border border-[#2e3b43] px-2.5 text-[11px]! font-medium leading-none! transition-colors"
            :class="showPreLive ? 'border-[#f28c28] bg-[#f28c28] text-[#241403]!' : 'bg-transparent text-[#cbd5da]! hover:bg-[#212a2f] hover:text-[#e9eef1]!'"
            @click="showPreLive = !showPreLive"
          >
            Pre-live ({{ phaseCounts['pre-live'] }})
          </button>
        </div>

        <button
          v-if="canSeeAllAccounts"
          class="inline-flex h-[26px] shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-xs border border-[#2e3b43] px-2.5 text-[11px]! font-medium leading-none! transition-colors"
          :class="allAccounts ? 'border-[#2f6f9f] bg-[#2f6f9f] text-[#eaf3fa]!' : 'bg-transparent text-[#cbd5da]! hover:bg-[#212a2f] hover:text-[#e9eef1]!'"
          :title="allAccounts ? 'Showing events of every client account' : `Showing ${accountName} only`"
          @click="allAccounts = !allAccounts"
        >
          All events
        </button>

        <button
          class="inline-flex h-[26px] shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-xs border border-[#2e3b43] px-2.5 text-[11px]! font-medium leading-none! transition-colors"
          :class="issuesOnly ? 'border-[#d9a520] bg-[#d9a520] text-[#241d03]!' : 'bg-transparent text-[#cbd5da]! hover:bg-[#212a2f] hover:text-[#e9eef1]!'"
          @click="issuesOnly = !issuesOnly"
        >
          Issues ({{ eventsWithIssuesCount }})
        </button>

        <button
          class="inline-flex h-[26px] w-[26px] shrink-0 cursor-pointer items-center justify-center rounded-xs border border-[#2e3b43] bg-transparent text-[11px]! text-[#cbd5da]! transition-colors hover:bg-[#212a2f] hover:text-[#e9eef1]! disabled:cursor-default disabled:opacity-50"
          title="Refresh"
          :disabled="busy"
          @click="refresh"
        >
          <ma-loading class="bly-loading" :class="{ 'animate-spin': busy }"></ma-loading>
        </button>
      </div>

      <div class="hidden shrink-0 items-center gap-2 max-[1100px]:flex">
        <button
          class="inline-flex h-[26px] w-[26px] shrink-0 cursor-pointer items-center justify-center rounded-xs border border-[#2e3b43] bg-transparent text-[11px]! text-[#cbd5da]! transition-colors hover:bg-[#212a2f] hover:text-[#e9eef1]! disabled:cursor-default disabled:opacity-50"
          title="Refresh"
          :disabled="busy"
          @click="refresh"
        >
          <ma-loading class="bly-loading" :class="{ 'animate-spin': busy }"></ma-loading>
        </button>
        <button
          class="relative inline-flex h-[26px] w-[26px] shrink-0 cursor-pointer items-center justify-center rounded-xs border border-[#2e3b43] text-[11px]! transition-colors"
          :class="mobileFiltersActive ? 'border-[#2f8f52] bg-[#2f8f52] text-[#eafaf0]!' : 'bg-transparent text-[#cbd5da]! hover:bg-[#212a2f] hover:text-[#e9eef1]!'"
          title="Open filters"
          @click="openFiltersDrawer"
        >
          <i class="el-icon-s-operation"></i>
          <span
            v-if="mobileFiltersCount"
            class="absolute -right-[5px] -top-[5px] h-3.5 min-w-3.5 rounded-full bg-[#ff9f31] px-[3px] text-[9px] font-bold leading-[14px] text-[#241403]"
          >
            {{ mobileFiltersCount }}
          </span>
        </button>
      </div>
    </div>

    <!-- Mobile filters drawer -->
    <transition name="source-monitoring-drawer">
      <div
        v-if="isFiltersDrawerOpen"
        class="fixed inset-0 z-50 top-12"
      >
        <button
          class="absolute inset-0 bg-slate-950/75 backdrop-blur-[1px]"
          aria-label="Close filters"
          @click="closeFiltersDrawer"
        ></button>

        <div class="absolute right-0 top-0 h-full w-full max-w-sm overflow-y-auto bg-[#13191d] shadow-2xl">
          <div class="flex items-center justify-between px-5 py-4">
            <p class="text-sm font-semibold text-slate-100">Filters</p>
            <button
              class="h-9 w-9 flex items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
              aria-label="Close filters"
              @click="closeFiltersDrawer"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>

          <div class="space-y-5 px-5 py-5">
            <div class="space-y-2">
              <label class="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Search
              </label>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search events, inputs, outputs..."
                class="h-10 w-full rounded border border-white/10 bg-[#13191d] px-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Phase
              </label>
              <div class="grid grid-cols-2 gap-2">
                <button
                  class="h-10 rounded border text-xs font-bold tracking-tight transition-colors"
                  :class="showLive
                    ? 'border-red-500 bg-red-700/30 text-red-200'
                    : 'border-white/10 bg-[#13191d] text-slate-200 hover:bg-slate-800'"
                  @click="showLive = !showLive"
                >
                  Live ({{ phaseCounts.live }})
                </button>
                <button
                  class="h-10 rounded border text-xs font-bold tracking-tight transition-colors"
                  :class="showPreLive
                    ? 'border-amber-500 bg-amber-700/30 text-amber-200'
                    : 'border-white/10 bg-[#13191d] text-slate-200 hover:bg-slate-800'"
                  @click="showPreLive = !showPreLive"
                >
                  Pre-live ({{ phaseCounts['pre-live'] }})
                </button>
              </div>
            </div>

            <div v-if="canSeeAllAccounts" class="space-y-2">
              <label class="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Accounts
              </label>
              <button
                class="flex h-10 w-full items-center justify-between rounded border px-3 text-sm font-semibold transition-colors"
                :class="allAccounts
                  ? 'border-sky-500 bg-sky-700/20 text-sky-300'
                  : 'border-white/10 bg-[#13191d] text-slate-200 hover:bg-slate-800'"
                @click="allAccounts = !allAccounts"
              >
                <span>{{ allAccounts ? 'View all events' : accountName }}</span>
                <span class="text-xs uppercase tracking-[0.16em]">{{ allAccounts ? 'All' : 'Account' }}</span>
              </button>
            </div>

            <div class="space-y-2">
              <label class="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Issues
              </label>
              <button
                class="flex h-10 w-full items-center justify-between rounded border px-3 text-sm font-semibold transition-colors"
                :class="issuesOnly
                  ? 'border-yellow-500 bg-yellow-700/20 text-yellow-200'
                  : 'border-white/10 bg-[#13191d] text-slate-200 hover:bg-slate-800'"
                @click="issuesOnly = !issuesOnly"
              >
                <span>Only events with issues</span>
                <span class="text-xs uppercase tracking-[0.16em]">{{ issuesOnly ? 'On' : 'Off' }}</span>
              </button>
            </div>

            <div class="flex items-center gap-2 pt-2">
              <button
                class="flex h-10 flex-1 items-center justify-center gap-2 rounded border border-white/10 bg-[#13191d] text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800"
                @click="resetFilters"
              >
                Reset
              </button>
              <button
                class="flex h-10 flex-1 items-center justify-center gap-2 rounded bg-yellow-600 text-sm font-semibold text-black! transition-colors hover:bg-yellow-500"
                @click="closeFiltersDrawer"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Event List -->
    <!-- min-height:0 or a flex child refuses to shrink below its content and scrolls the page. -->
    <div v-bar class="min-h-0 flex-1">
      <div>
        <div v-if="loadingLiveAssignments && monitoredEvents.length === 0" class="text-center text-slate-500 py-12">
          Loading events...
        </div>

        <div v-else-if="eventsError" class="text-center text-red-400 py-12">
          Couldn't load events: {{ eventsError }}
        </div>

        <div v-else-if="filteredEvents.length === 0" class="text-center text-slate-500 py-12">
          <span v-if="monitoredEvents.length === 0">No events in the last / next 4 days</span>
          <span v-else>No events match the current filters</span>
        </div>

        <div v-else class="flex flex-col gap-2 px-3.5 py-2.5">
          <div
            v-for="event in filteredEvents"
            :key="event.id"
            class="relative space-y-2 transition-colors cursor-pointer rounded-sm p-4 roundedn-sm bg-[#232b33]/70 hover:bg-[#232b33]/40 text-[#e9eef1]"
            @click="toggleExpanded(event)"
          >
            <!-- Booking status strip; colour comes from the shared bg-<STATUS> palette -->
            <div
              class="absolute inset-y-2.5 left-0 w-[3px] rounded-xs"
              :class="event.status ? `bg-${event.status}` : 'bg-[#46525c]'"
              :title="event.status"
            ></div>

            <!-- Header: phase, title, then client account block on the right -->
            <div class="mt-0! flex min-w-0 items-center gap-2">
              <span
                v-if="eventMonitoringStateClass(event)"
                class="inline-flex h-[18px] shrink-0 items-center rounded-xs border px-[7px] text-[9.5px] font-semibold uppercase tracking-[0.8px]"
                :class="eventMonitoringStateClass(event)"
              >
                {{ phaseOf(event) }}
              </span>
              <span
                v-else-if="event.status"
                class="inline-flex h-[18px] shrink-0 items-center rounded-xs border border-[#2e3b43] bg-[#1a2226] px-[7px] text-[9.5px] font-semibold uppercase tracking-[0.8px]"
                :class="`text-${event.status}`"
              >
                {{ event.status }}
              </span>

              <span class="min-w-0 truncate text-sm font-semibold" :title="event.status">{{ eventTitle(event) }}</span>
              <span v-if="eventSubtitle(event)" class="min-w-0 shrink truncate text-[11.5px] text-[#75838b]">
                {{ eventSubtitle(event) }}
              </span>

              <span
                v-if="issuesOf(event).length"
                class="inline-flex h-[15px] shrink-0 items-center rounded-xs bg-[#8b1a1a] px-[5px] text-[9px] uppercase tracking-[0.5px] text-[#ffc9c9]"
              >
                {{ issuesOf(event).length }} {{ issuesOf(event).length === 1 ? 'issue' : 'issues' }}
              </span>
              <span
                v-if="event.liveMonitoring"
                class="inline-flex h-[15px] shrink-0 items-center rounded-xs bg-[#2f8f52] px-[5px] text-[9px] uppercase tracking-[0.5px] text-[#eafaf0]"
              >
                monitoring
              </span>

              <div class="ml-auto flex shrink-0 items-center gap-2">
                <div class="flex flex-col items-end gap-[3px]">
                  <div class="flex items-center gap-1.5 whitespace-nowrap text-xs">
                    <span class="font-semibold text-[#e9eef1]">{{ event.customer ? event.customer.name : '-' }}</span>
                    <span class="text-[9px] uppercase tracking-[0.9px] text-[#ff9f31]">/</span>
                    <span class="text-[#a9b6bd]">{{ event.account ? event.account.name : '-' }}</span>
                  </div>
                  <div class="flex items-center gap-1.5 whitespace-nowrap text-xs">
                    <button
                      class="cursor-pointer border-0 bg-transparent p-0 text-[10.5px]! text-[#ff9f31]! hover:text-[#ffb763]!"
                      @click.stop="openEventReport(event)"
                    >
                      Report
                    </button>
                    <span
                      v-for="tag in event.tags || []"
                      :key="tag"
                      class="rounded-xs border border-[#6082b4]/45 px-1.5 py-px text-[9.5px] tracking-[0.4px] text-[#b8cbe6]"
                    >
                      {{ tag }}
                    </span>
                  </div>
                </div>

                <button
                  class="grid h-6 w-6 cursor-pointer place-items-center rounded-xs border-0 bg-transparent p-0 text-[13px]! text-[#75838b]! hover:bg-[#212a2f] hover:text-[#e9eef1]!"
                  :title="isExpanded(event) ? 'Collapse' : 'Expand'"
                  @click.stop="toggleExpanded(event)"
                >
                  <i :class="isExpanded(event) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                </button>
              </div>
            </div>

            <!-- Cards: countdown, input, outputs -->
            <div class="flex min-w-0 flex-wrap gap-2">
              <div class="relative flex w-[152px] shrink-0 flex-col items-start gap-px rounded  bg-black/30 px-2.5 py-2 pt-2.5">
                <span
                  class="absolute -top-[7px] left-2 rounded-xs bg-[#2b343b] px-[5px] text-[8.5px] font-semibold uppercase leading-[14px] tracking-[0.8px]"
                  :class="countdownOf(event).labelClass"
                >
                  {{ countdownOf(event).label }}
                </span>
                <span
                  class="font-mono text-lg leading-[1.15] tabular-nums"
                  :class="countdownOf(event).valueClass"
                >
                  {{ formatDuration(countdownOf(event).ms) }}
                </span>
                <span class="font-mono text-[10px] tabular-nums text-[#6d7a82]">{{ eventDateRangeText(event) }}</span>
              </div>

              <!-- INPUT -->
              <div class="relative flex min-w-0 shrink grow basis-[300px] items-center gap-[9px] rounded bg-black/30 px-2 py-3">
                <span class="absolute -top-[7px] left-2 rounded-xs bg-[#2b343b] px-[5px] text-[8.5px] font-semibold uppercase leading-[14px] tracking-[0.8px] text-[#c4d0d7]">
                  IN
                </span>

                <button
                  class="relative grid h-[38px] w-16 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-xs  bg-black p-0"
                  :title="eventInput(event) ? thumbnailTooltip(eventInput(event)) : 'No input assigned'"
                  @click.stop="openInputDetails(event)"
                >
                  <template v-if="eventInput(event)">
                    <img
                      v-if="getThumbnailData(eventInput(event)).data && !getThumbnailData(eventInput(event)).error && !getThumbnailData(eventInput(event)).warning"
                      :src="getThumbnailData(eventInput(event)).data"
                      :alt="`${eventInputName(event)} thumbnail`"
                      class="h-full w-full object-cover"
                    />
                    <ma-loading
                      v-else-if="getThumbnailData(eventInput(event)).loading"
                      class="bly-loading animate-spin text-xs text-[#4d585f]"
                    ></ma-loading>
                    <ma-warning
                      v-else-if="getThumbnailData(eventInput(event)).warning"
                      class="bly-warning text-xs text-[#ffb763]"
                    ></ma-warning>
                    <i
                      v-else-if="getThumbnailData(eventInput(event)).error"
                      class="bi bly-alert text-xs text-[#ff5f5f]"
                    ></i>
                    <span v-else class="text-[8.5px] uppercase tracking-[0.6px] text-[#4d585f]">no <br> preview</span>
                    <span
                      v-if="thumbnailAge(eventInput(event))"
                      class="absolute bottom-0.5 right-0.5 rounded-xs bg-[#080c0e]/75 px-[3px] font-mono text-[8.5px] text-[#cbd5da]"
                    >
                      {{ thumbnailAge(eventInput(event)) }}
                    </span>
                  </template>
                  <span v-else class="text-[8.5px] uppercase tracking-[0.6px] text-[#4d585f]">no input</span>
                </button>

                <div class="flex min-w-0 flex-1 flex-col gap-1">
                  <div class="flex min-w-0 items-center gap-1.5">
                    <template v-if="eventInput(event) || event.inputProfile">
                      <span
                        class="h-2 w-2 shrink-0 rounded-full"
                        :style="{ background: eventInput(event) ? statusSquareColor(eventInput(event)) : '#4d585f' }"
                        :title="eventInput(event) ? statusTooltip(eventInput(event)) : 'No input assigned to the profile'"
                      ></span>
                      <button
                        class="min-w-0 cursor-pointer truncate border-0 bg-transparent p-0 text-left text-xs! text-[#cbd5da]! hover:text-[#e9eef1]!"
                        :title="eventInputLabel(event)"
                        @click.stop="openInputDetails(event)"
                      >
                        {{ eventInputLabel(event) }}
                      </button>
                      <!-- With a profile the resolved input is the detail, not the headline. -->
                      <span
                        v-if="eventInputSubLabel(event)"
                        class="min-w-0 shrink truncate text-[10.5px] text-[#75838b]"
                        :title="eventInputSubLabel(event)"
                      >
                        {{ eventInputSubLabel(event) }}
                      </span>
                    </template>
                    <span v-else class="text-xs text-[#75838b]">No input assigned</span>
                  </div>

                  <div class="flex min-w-0 items-center gap-1.5">
                    <span
                      v-if="eventInput(event) && eventInput(event).type"
                      class="shrink-0 rounded-xs border border-[#6082b4]/45 px-1.5 py-px text-[9.5px] tracking-[0.3px] text-[#b8cbe6] whitespace-nowrap"
                    >
                      {{ eventInput(event).type }}
                    </span>

                    <!-- The booked input was swapped out; ops must not chase the original. -->
                    <span
                      v-if="event.inputOverride"
                      class="shrink-0 rounded-xs border border-[#f28c28]/50 px-1.5 py-px text-[9.5px] tracking-[0.3px] text-[#ffb763] whitespace-nowrap"
                      :title="event.input ? `Booked as ${event.input.name}` : 'Input override'"
                    >
                      override
                    </span>
                    <span
                      v-if="event.inputProfileOverride"
                      class="shrink-0 rounded-xs border border-[#f28c28]/50 px-1.5 py-px text-[9.5px] tracking-[0.3px] text-[#ffb763] whitespace-nowrap"
                      title="Per-booking ingest config, overriding the input profile"
                    >
                      config
                    </span>
                    <span
                      v-if="inputConnectionConfig(event)"
                      class="min-w-0 truncate font-mono text-[9.5px] text-[#75838b]"
                      :title="inputConnectionTooltip(event)"
                    >
                      {{ inputConnectionLabel(event) }}
                    </span>

                    <!-- Provider / input details; the row toggles on click, the popover must not. -->
                    <el-popover
                      v-if="eventProvider(event) || event.inputProfile || event.inputDetails"
                      @click.native.stop
                      placement="bottom-start"
                      class="relative -top-0.5"
                      width="380"
                      trigger="click"
                    >
                      <div class="text-xs leading-relaxed">
                        <div v-if="eventProvider(event)">
                          <div class="font-bold">{{ eventProvider(event).name }}</div>
                          <div v-if="eventProvider(event).contactDetails" class="mt-1 whitespace-pre-line">
                            {{ eventProvider(event).contactDetails }}
                          </div>
                          <div v-if="eventProvider(event).instructions" class="mt-1 whitespace-pre-line opacity-70">
                            {{ eventProvider(event).instructions }}
                          </div>
                        </div>
                        <div v-if="event.inputProfile" class="mt-2 opacity-70">
                          Profile: {{ event.inputProfile.name }}
                          <span v-if="event.inputProfile.location"> &middot; {{ parseLocation(event.inputProfile.location) }}</span>
                        </div>
                        <div v-if="inputConnectionConfig(event)" class="mt-2 border-t pt-2">
                          <span class="opacity-60">Ingest:</span>
                          {{ inputConnectionTooltip(event) }}
                        </div>
                        <div v-if="event.inputDetails" class="mt-2 whitespace-pre-line border-t pt-2">
                          {{ event.inputDetails }}
                        </div>
                      </div>
                      <button
                        slot="reference"
                        class="shrink-0 cursor-pointer rounded-xs border border-[#ff9f31]/45 bg-transparent px-1.5 py-px text-[9.5px]! tracking-[0.3px] text-[#ff9f31]! whitespace-nowrap hover:bg-[#ff9f31]/10"
                        title="Provider &amp; input details"
                      >
                        {{ eventProvider(event) ? eventProvider(event).name : 'input details' }}
                      </button>
                    </el-popover>
                  </div>
                </div>
              </div>

              <!-- OUTPUTS, one card; the OUT tag switches between them -->
              <div
                v-for="output in visibleOutputs(event)"
                :key="`${event.id}-${output.id}`"
                class="relative flex min-w-0 shrink grow basis-[300px] items-center gap-[9px] rounded  bg-black/30 px-2 py-2 pt-3"
              >
                <span class="absolute -top-[7px] left-2 flex items-center gap-1">
                  <button
                    v-for="(item, index) in switchableOutputs(event)"
                    :key="item.id"
                    class="cursor-pointer rounded-xs border-0 px-[5px] text-[8.5px]! font-semibold uppercase leading-[14px]! tracking-[0.8px]"
                    :class="index === outputIndexOf(event)
                      ? 'bg-[#3a4650] text-[#ffcf8f]!'
                      : 'bg-[#232c33] text-[#7f8c95]! hover:text-[#c4d0d7]!'"
                    :title="item.displayName || item.name"
                    @click.stop="selectOutput(event, index)"
                  >
                    Out {{ index + 1 }}
                  </button>
                  <span
                    v-if="!switchableOutputs(event).length"
                    class="rounded-xs bg-[#2b343b] px-[5px] text-[8.5px] font-semibold uppercase leading-[14px] tracking-[0.8px] text-[#c4d0d7]"
                  >
                    Out
                  </span>
                </span>

                <button
                  v-if="eventHlsUrls(event, output.id).length"
                  class="grid h-[38px] w-16 shrink-0 cursor-pointer place-items-center rounded-xs border border-[#3ddc84]/30 bg-[#3ddc84]/10 p-0 text-base! text-[#3ddc84]! hover:bg-[#3ddc84]/20"
                  :title="`Play ${output.displayName || output.name}`"
                  @click.stop="openPlayer(eventHlsUrls(event, output.id)[0], output.displayName || output.name)"
                >
                  <i class="el-icon-video-play"></i>
                </button>
                <span
                  v-else
                  class="grid h-[38px] w-16 shrink-0 place-items-center rounded-xs  bg-black text-base text-[#3f4a51]"
                  title="No playback URL"
                >
                  <i class="el-icon-video-play"></i>
                </span>

                <div class="flex min-w-0 flex-1 flex-col gap-1">
                  <div class="flex min-w-0 items-center gap-1.5">
                    <span
                      class="h-2 w-2 shrink-0 rounded-full"
                      :style="{ background: outputSquareColor(output) }"
                      :title="outputStatusTooltip(output)"
                    ></span>
                    <button
                      class="min-w-0 flex-1 cursor-pointer truncate border-0 bg-transparent p-0 text-left text-xs! text-[#cbd5da]! hover:text-[#e9eef1]!"
                      @click.stop="openOutputDetails(output)"
                    >
                      {{ output.displayName || output.name }}
                    </button>
                  </div>

                  <div class="flex min-w-0 items-center gap-1.5">
                    <span class="shrink-0 rounded-xs border border-[#6082b4]/45 px-1.5 py-px text-[9.5px] tracking-[0.3px] text-[#b8cbe6] whitespace-nowrap">
                      {{ output.outputType }}
                    </span>
                    <a
                      v-for="(url, urlIndex) in eventHlsUrls(event, output.id).slice(1)"
                      :key="url"
                      class="shrink-0 cursor-pointer text-[13px] leading-none text-[#ff9f31] hover:text-[#ffb763]"
                      :title="url"
                      @click.stop="openPlayer(url, output.displayName || output.name)"
                    >
                      <i class="el-icon-video-play"></i>{{ urlIndex + 2 }}
                    </a>
                  </div>
                </div>
              </div>

              <div
                v-if="eventOutputs(event).length === 0"
                class="relative flex min-w-0 shrink grow basis-[300px] items-center gap-[9px] rounded  bg-black/30 px-2 py-2 pt-3"
              >
                <span class="absolute -top-[7px] left-2 rounded-xs bg-[#2b343b] px-[5px] text-[8.5px] font-semibold uppercase leading-[14px] tracking-[0.8px] text-[#c4d0d7]">
                  OUT
                </span>
                <span class="grid h-[38px] w-16 shrink-0 place-items-center rounded-xs  bg-black text-base text-[#3f4a51]">
                  <i class="el-icon-video-play"></i>
                </span>
                <span class="text-xs text-[#75838b]">No outputs assigned</span>
              </div>
            </div>

            <!-- Details, on click only -->
            <template v-if="isExpanded(event)">
              <div
                v-if="issuesOf(event).length"
                class="min-w-0 rounded border border-[#232e34] bg-black/30 px-3.5 py-3"
              >
                <p class="mb-2.5 text-[9.5px] uppercase tracking-[1.4px] text-[#6d7a82]">Issues</p>
                <div
                  v-for="(issue, issueIndex) in issuesOf(event)"
                  :key="issueIndex"
                  class="flex items-start gap-1.5 text-xs leading-relaxed"
                  :class="issue.level === 'error' ? 'text-red-400' : 'text-yellow-400'"
                >
                  <span class="shrink-0">{{ issue.level === 'error' ? '✕' : '⚠' }}</span>
                  <span>
                    <span class="opacity-60">{{ issue.scope }} &middot; {{ issue.name }} &mdash;</span>
                    {{ issue.message }}
                  </span>
                </div>
              </div>

              <div class="flex flex-wrap gap-2">
                <!-- INPUT -->
                <div class="min-w-0 shrink grow basis-[420px] rounded ] bg-black/30 px-3.5 py-3">
                  <p class="mb-2.5 text-[9.5px] uppercase tracking-[1.4px] text-[#6d7a82]">Input &middot; connection details</p>

                  <div class="mb-3 flex items-start gap-3">
                    <button
                      v-if="eventInput(event)"
                      class="relative grid h-[72px] w-32 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-xs border border-[#232e34] bg-[#10161a] p-0"
                      :title="thumbnailTooltip(eventInput(event))"
                      @click.stop="openInputDetails(event)"
                    >
                      <img
                        v-if="getThumbnailData(eventInput(event)).data"
                        :src="getThumbnailData(eventInput(event)).data"
                        :alt="`${eventInputName(event)} thumbnail`"
                        class="h-full w-full object-cover"
                      />
                      <span v-else class="text-[8.5px] uppercase tracking-[0.6px] text-[#4d585f]">no preview</span>
                      <span
                        v-if="thumbnailAge(eventInput(event))"
                        class="absolute bottom-0.5 right-0.5 rounded-xs bg-[#080c0e]/75 px-[3px] font-mono text-[8.5px] text-[#cbd5da]"
                      >
                        {{ thumbnailAge(eventInput(event)) }}
                      </span>
                    </button>

                    <div class="flex min-w-0 flex-col gap-1">
                      <div class="flex min-w-0 items-center gap-1.5">
                        <span
                          class="h-2 w-2 shrink-0 rounded-full"
                          :style="{ background: eventInput(event) ? statusSquareColor(eventInput(event)) : '#4d585f' }"
                        ></span>
                        <span class="text-xs leading-relaxed text-[#cbd5da]">
                          auto-refresh {{ thumbnailRefreshLabel }}
                        </span>
                      </div>
                      <span class="text-xs leading-relaxed text-[#cbd5da]">
                        {{ eventInputLabel(event) || 'No input assigned' }}
                      </span>
                      <span v-if="eventInputSubLabel(event)" class="text-xs leading-relaxed text-[#75838b]">
                        {{ eventInputSubLabel(event) }}
                      </span>
                    </div>
                  </div>

                  <div
                    v-for="row in inputDetailRows(event)"
                    :key="row.label"
                    class="flex min-w-0 items-baseline gap-2.5 py-px"
                  >
                    <span class="shrink-0 basis-[104px] text-xs text-[#6d7a82]">{{ row.label }}</span>
                    <span class="min-w-0 flex-1 break-words text-xs leading-relaxed text-[#cbd5da]">
                      {{ row.value }}
                    </span>
                  </div>

                  <div v-if="inputMetricsUrl(event)" class="flex min-w-0 items-baseline gap-2.5 py-px">
                    <span class="shrink-0 basis-[104px] text-xs text-[#6d7a82]">Metrics</span>
                    <span class="min-w-0 flex-1 text-xs leading-relaxed">
                      <a
                        :href="inputMetricsUrl(event)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="cursor-pointer normal-case! text-[#cbd5da] hover:text-[#ff9f31]"
                        @click.stop
                      >
                        Dashboard &#8599;
                      </a>
                    </span>
                  </div>

                  <template v-if="eventProvider(event) || providerRows(event).length">
                    <div class="my-2.5 border-t border-dashed border-[#2b3238]"></div>
                    <p v-if="eventProvider(event)" class="mb-1.5 text-xs text-[#ff9f31]">
                      {{ eventProvider(event).name }}
                    </p>
                    <div
                      v-for="row in providerRows(event)"
                      :key="row.label"
                      class="flex min-w-0 items-baseline gap-2.5 py-px"
                    >
                      <span class="shrink-0 basis-[104px] text-xs text-[#6d7a82]">{{ row.label }}</span>
                      <span class="min-w-0 flex-1 whitespace-pre-line break-words text-xs leading-relaxed text-[#cbd5da]">
                        {{ row.value }}
                      </span>
                    </div>
                  </template>
                </div>

                <!-- OUTPUT -->
                <div class="min-w-0 shrink grow basis-[420px] rounded  bg-black/30 px-3.5 py-3">
                  <p class="mb-2.5 text-[9.5px] uppercase tracking-[1.4px] text-[#6d7a82]">Output &middot; delivery</p>

                  <template v-for="(output, outputIndex) in eventOutputs(event)">
                    <div
                      v-if="outputIndex"
                      :key="`${output.id}-sep`"
                      class="my-2.5 border-t border-dashed border-[#2b3238]"
                    ></div>
                    <div
                      v-for="row in outputDetailRows(output)"
                      :key="`${output.id}-${row.label}`"
                      class="flex min-w-0 items-baseline gap-2.5 py-px"
                    >
                      <span class="shrink-0 basis-[104px] text-xs text-[#6d7a82]">{{ row.label }}</span>
                      <span class="min-w-0 flex-1 break-words text-xs leading-relaxed text-[#cbd5da]">
                        {{ row.value }}
                      </span>
                    </div>
                    <div
                      v-if="outputMetricsUrl(output) || getGrafanaMediaLiveChannelUrl(output)"
                      :key="`${output.id}-metrics`"
                      class="flex min-w-0 items-baseline gap-2.5 py-px"
                    >
                      <span class="shrink-0 basis-[104px] text-xs text-[#6d7a82]">Metrics</span>
                      <span class="min-w-0 flex-1 text-xs leading-relaxed">
                        <a
                          v-if="outputMetricsUrl(output)"
                          :href="outputMetricsUrl(output)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="cursor-pointer normal-case! text-[#cbd5da] hover:text-[#ff9f31]"
                          @click.stop
                        >
                          Dashboard &#8599;
                        </a>
                        <a
                          v-if="getGrafanaMediaLiveChannelUrl(output)"
                          :href="getGrafanaMediaLiveChannelUrl(output)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="ml-2.5 cursor-pointer normal-case! text-[#cbd5da] hover:text-[#ff9f31]"
                          @click.stop
                        >
                          ML channel &#8599;
                        </a>
                      </span>
                    </div>
                    <div
                      v-for="url in eventHlsUrls(event, output.id)"
                      :key="url"
                      class="flex min-w-0 items-baseline gap-2.5 py-px"
                    >
                      <span class="shrink-0 basis-[104px] text-xs text-[#6d7a82]">Playback</span>
                      <span class="min-w-0 flex-1 break-words text-xs leading-relaxed text-[#cbd5da]">
                        <a
                          class="cursor-pointer normal-case! text-[#cbd5da] hover:text-[#ff9f31]"
                          :title="url"
                          @click.stop="openPlayer(url, output.displayName || output.name)"
                        >
                          {{ url }}
                        </a>
                        <button
                          class="ml-1.5 cursor-pointer rounded-xs border border-[#2e3b43] bg-transparent px-1.5 py-px text-[9.5px]! uppercase tracking-[0.3px] text-[#a9b6bd]! hover:bg-[#212a2f] hover:text-[#e9eef1]!"
                          @click.stop="copyToClipboard(url)"
                        >
                          copy
                        </button>
                      </span>
                    </div>
                  </template>

                  <p v-if="eventOutputs(event).length === 0" class="text-xs text-[#cbd5da]">
                    No outputs assigned
                  </p>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <HlsPlayer :visible.sync="player.visible" :url="player.url" :name="player.name" />
    <transition name="source-monitoring-panel">
      <div
        v-if="selectedMatrixItem && selectedMatrixEntity"
        class="fixed inset-0 z-50 top-11"
      >
        <button
          class="absolute inset-0 bg-slate-950/80 backdrop-blur-[1px]"
          aria-label="Close details"
          @click="closeMatrixDetails"
        >

        </button>

        <div class="absolute right-0 top-0 h-full w-full max-w-sm overflow-y-auto bg-[#13191d] shadow-2xl">
          <div class="border-b border-white/10 px-5 py-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {{ selectedMatrixItem.tab === 'inputs' ? 'Input details' : 'Output details' }}
                </p>
                <h3 class="mt-1 truncate text-lg font-semibold text-slate-100">
                  {{ selectedMatrixItem.tab === 'inputs' ? selectedMatrixEntity.name : (selectedMatrixEntity.name || selectedMatrixEntity.displayName) }}
                </h3>
                <div class="mt-2 flex flex-wrap gap-2">
                  <span
                    class="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                    :class="selectedMatrixItem.tab === 'inputs'
                      ? inputTypeBadgeClass(selectedMatrixEntity.type)
                      : 'bg-orange-900/40 text-orange-400'"
                  >
                    {{ selectedMatrixItem.tab === 'inputs' ? selectedMatrixEntity.type : selectedMatrixEntity.outputType }}
                  </span>
                  <span class="rounded-full bg-slate-800/60 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-300">
                    {{ selectedMatrixItem.tab === 'inputs' ? (getInputState(selectedMatrixEntity.id) || 'UNKNOWN') : (getOutputState(selectedMatrixEntity.id) || 'UNKNOWN') }}
                  </span>
                  <span
                    class="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                    :class="selectedMatrixItem.tab === 'inputs'
                      ? statusLabelClass(selectedMatrixEntity)
                      : outputStatusLabelClass(selectedMatrixEntity)"
                  >
                    {{ selectedMatrixItem.tab === 'inputs' ? statusLabel(selectedMatrixEntity) : outputStatusLabel(selectedMatrixEntity) }}
                  </span>
                </div>
              </div>

              <button
                class="h-9 w-9 shrink-0 flex items-center justify-center rounded border border-white/10 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
                aria-label="Close details"
                @click="closeMatrixDetails"
              >
                 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48" id="Delete-1--Streamline-Plump" height="48" width="48" class="w-3 h-3">
                  <g id="Solid/Interface Essential/Add Remove Delete/delete-1--remove-add-button-buttons-delete-cross-x-mathematics-multiply-math">
                    <path id="Union" fill="currentColor" d="M44.7021 0.7704c-0.6868 -0.3728 -1.3995 -0.3517 -2.035 -0.1323 -0.6219 0.2148 -1.1856 0.6222 -1.6463 1.0605 -2.5083 2.387 -8.5323 8.1658 -17.0209 16.6073C15.5115 9.8644 9.4874 4.0856 6.9791 1.6986 6.5186 1.2603 5.9548 0.8529 5.3329 0.6381 4.6974 0.4187 3.9847 0.3976 3.2979 0.7704c-0.4425 0.2401 -0.9254 0.5908 -1.431 1.0964 -0.5055 0.5055 -0.8562 0.9885 -1.0964 1.4309 -0.3728 0.6868 -0.3517 1.3996 -0.1322 2.0351 0.2147 0.6219 0.6222 1.1856 1.0605 1.6462 2.3869 2.5083 8.1657 8.5324 16.6073 17.0209C9.8644 32.4886 4.0854 38.5127 1.6984 41.0211c-0.4382 0.4605 -0.8457 1.0242 -1.0604 1.6461 -0.2195 0.6356 -0.2406 1.3483 0.1322 2.0351 0.2402 0.4424 0.5909 0.9254 1.0964 1.431 0.5056 0.5054 0.9885 0.8561 1.431 1.0964 0.6868 0.3727 1.3995 0.3516 2.035 0.1322 0.6219 -0.2148 1.1856 -0.6222 1.6462 -1.0605C9.4871 43.9144 15.5113 38.1356 24 29.6938c8.4887 8.4418 14.5129 14.2206 17.0212 16.6076 0.4605 0.4383 1.0242 0.8457 1.6461 1.0605 0.6356 0.2194 1.3483 0.2405 2.0351 -0.1322 0.4424 -0.2403 0.9254 -0.591 1.431 -1.0964 0.5055 -0.5056 0.8562 -0.9886 1.0964 -1.431 0.3728 -0.6868 0.3517 -1.3995 0.1322 -2.0351 -0.2147 -0.6219 -0.6222 -1.1856 -1.0605 -1.6461 -2.387 -2.5084 -8.1658 -8.5325 -16.6076 -17.0212C38.1354 15.5114 43.9143 9.4873 46.3012 6.979c0.4384 -0.4606 0.8457 -1.0243 1.0605 -1.6462 0.2195 -0.6355 0.2406 -1.3483 -0.1323 -2.0351 -0.2401 -0.4424 -0.5908 -0.9254 -1.0964 -1.4309 -0.5055 -0.5055 -0.9884 -0.8562 -1.4309 -1.0964Z" stroke-width="1"></path>
                  </g>
                </svg>
              </button>
            </div>
          </div>

          <div class="space-y-5 p-5">
            <div class="overflow-hidden rounded-md border border-white/10 bg-slate-900/80">
              <div
                v-if="selectedMatrixItem.tab === 'inputs'"
                class="relative aspect-video bg-slate-900/80"
              >
                <img
                  v-if="getThumbnailData(selectedMatrixEntity).data && !getThumbnailData(selectedMatrixEntity).error"
                  :src="getThumbnailData(selectedMatrixEntity).data"
                  :alt="`${selectedMatrixEntity.name} thumbnail`"
                  class="h-full w-full object-cover"
                />
                <div
                  v-else-if="getThumbnailData(selectedMatrixEntity).loading"
                  class="flex h-full w-full items-center justify-center text-slate-400"
                >
                  <ma-loading class="bly-loading animate-spin text-lg"></ma-loading>
                </div>
                <div
                  v-else
                  class="flex h-full w-full items-center justify-center text-slate-500"
                >
                  <ma-image class="bly-image text-3xl"></ma-image>
                </div>
              </div>
              <div
                v-else
                class="relative flex aspect-video items-center justify-center"
              >
                <i class="bi bi-broadcast-pin text-6xl text-white/15"></i>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div class="rounded-md bg-black/19 p-4">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Connection</p>
                <p class="mt-2 text-sm text-slate-100">
                  {{ selectedMatrixItem.tab === 'inputs' ? matrixInputSummary(selectedMatrixEntity) : matrixOutputSummary(selectedMatrixEntity) }}
                </p>
                <p class="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">
                  {{ selectedMatrixItem.tab === 'inputs' ? matrixInputMeta(selectedMatrixEntity) : matrixOutputMeta(selectedMatrixEntity) }}
                </p>
              </div>

              <div class="rounded-md bg-black/19 p-4">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Location</p>
                <p class="mt-2 text-sm text-slate-100">
                  {{ selectedMatrixEntity.location ? parseLocation(selectedMatrixEntity.location) : 'No location assigned' }}
                </p>
                <p class="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">
                  {{ selectedMatrixItem.tab === 'inputs' ? (isInputLive(selectedMatrixEntity.id) ? 'Live event linked' : 'Not linked to live event') : (isOutputLive(selectedMatrixEntity.id) ? 'Live event linked' : 'Not linked to live event') }}
                </p>
                <button
                  v-if="matrixEntityEventId(selectedMatrixEntity, selectedMatrixItem.tab)"
                  class="mt-3 inline-flex items-center gap-1 rounded border border-amber-400/40 bg-amber-500/10 px-2 py-1 text-xs font-semibold text-amber-200 transition-colors hover:bg-amber-500/20"
                  @click="openResourceReport(selectedMatrixEntity, selectedMatrixItem.tab)"
                >
                  <i class="el-icon-document"></i>
                  Open report
                </button>
              </div>
            </div>

            <div class="rounded-md bg-black/19 p-4">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Connection details</p>

              <div
                v-if="selectedMatrixItem.tab === 'inputs'"
                class="mt-3 flex flex-wrap gap-2 text-xs"
              >
                <span
                  v-if="getProtocol(selectedMatrixEntity)"
                  class="rounded-full border border-white/10 bg-slate-800/60 px-2 py-1 text-slate-300"
                >
                  {{ getProtocol(selectedMatrixEntity) }}
                </span>
                <span
                  class="rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
                  :class="connectionStatusClass(selectedMatrixEntity)"
                >
                  {{ connectionStatusLabel(selectedMatrixEntity) }}
                </span>
                <span
                  v-for="source in getMediaConnectSources(selectedMatrixEntity)"
                  :key="`details-${selectedMatrixEntity.id}-${source.label}`"
                  class="rounded-full border px-2 py-1"
                  :class="source.connected
                    ? 'border-green-800/20 bg-green-900/20 text-green-300'
                    : 'border-red-800/20 bg-red-900/20 text-red-300'"
                >
                  {{ source.label }}: {{ source.connected ? source.ip : 'Not Connected' }}
                </span>
                <span
                  v-if="selectedMatrixInfo?.destination?.url"
                  class="rounded-full border border-indigo-800/20 bg-indigo-900/20 px-2 py-1 text-indigo-300"
                >
                  Destination: {{ selectedMatrixInfo.destination.url }}
                </span>
                <span
                  v-if="selectedMatrixInfo?.connectedClients != null"
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-slate-300"
                >
                  Clients: {{ selectedMatrixInfo.connectedClients }}
                </span>
                <span
                  v-if="selectedMatrixInfo?.RoutedOutputs != null"
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-slate-300"
                >
                  Outputs: {{ selectedMatrixInfo.RoutedOutputs }}
                </span>
              </div>

              <div
                v-else
                class="mt-3 flex flex-wrap gap-2 text-xs"
              >
                <span
                  v-if="getOutputProtocol(selectedMatrixEntity)"
                  class="rounded-full border border-white/10 bg-slate-800/60 px-2 py-1 text-slate-300"
                >
                  {{ getOutputProtocol(selectedMatrixEntity) }}
                </span>
                <span
                  v-if="getOutputDestinationIp(selectedMatrixEntity)"
                  class="rounded-full border border-green-800/20 bg-green-900/20 px-2 py-1 text-green-300"
                >
                  Destination IP: {{ getOutputDestinationIp(selectedMatrixEntity) }}
                </span>
                <span
                  v-if="selectedMatrixEntity.routedInput?.name"
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-slate-300"
                >
                  Routed input: {{ selectedMatrixEntity.routedInput.name }}
                </span>
                <span
                  v-if="getOutputRoutedState(selectedMatrixEntity)"
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-slate-300"
                >
                  Routed state: {{ getOutputRoutedState(selectedMatrixEntity) }}
                </span>
              </div>
            </div>

            <div class="flex flex-wrap gap-2">
              <a
                v-if="selectedMatrixItem.tab === 'inputs' && selectedMatrixInfo?.Name"
                :href="getGrafanaUrl(selectedMatrixInfo.Name, selectedMatrixInfo, selectedMatrixEntity)"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex h-10 items-center gap-2 rounded-full bg-blue-500/15 px-4 text-sm font-semibold text-blue-300 transition-colors hover:bg-blue-500/25 hover:text-blue-200"
              >
                <i class="bi bi-graph-up"></i>
                Metrics
              </a>
              <a
                v-if="selectedMatrixItem.tab === 'outputs' && selectedMatrixInfo?.Name"
                :href="getGrafanaOutputUrl(selectedMatrixInfo.Name, selectedMatrixInfo, selectedMatrixEntity)"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex h-10 items-center gap-2 rounded-full bg-blue-500/15 px-4 text-sm font-semibold text-blue-300 transition-colors hover:bg-blue-500/25 hover:text-blue-200"
              >
                <i class="bi bi-graph-up"></i>
                Metrics
              </a>
            </div>

            <div
              v-if="selectedMatrixItem.tab === 'inputs' ? getInputMessages(selectedMatrixEntity).length > 0 : getOutputMessages(selectedMatrixEntity).length > 0"
              class="rounded-md bg-black/19 p-4"
            >
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Messages</p>
              <div class="mt-3 flex flex-col gap-2">
                <div
                  v-for="(msg, idx) in selectedMatrixItem.tab === 'inputs' ? getInputMessages(selectedMatrixEntity) : getOutputMessages(selectedMatrixEntity)"
                  :key="idx"
                  class="flex items-start gap-2 text-sm"
                  :class="messageClass(msg)"
                >
                  <span class="mt-0.5 shrink-0">{{ messageIcon(msg) }}</span>
                  <span>{{ msg.Message }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import HlsPlayer from '@/components/HlsPlayer.vue';
import { getTranscodeDestinationUrls } from '@/services/eventTranscodeUrls';
import { monitoringStateOf } from '@/services/eventPhase';
import { parseInputProfileConfig } from '@/services/inputProfileConfig';
import { messageSeverity } from '@/utils/mediaMessages';

const OPS_MONITORING_FILTERS_KEY = 'ops-monitoring-filters';
const DEFAULT_FILTERS = {
  searchQuery: '',
  showLive: true,
  showPreLive: true,
  issuesOnly: false,
  // Off by default: a client with 50 bookings a day is unreadable when every
  // account is thrown into one list.
  allAccounts: false,
};

const STATUS_POLL_MS = 5000;
const EVENTS_POLL_MS = 30000;
const THUMBNAIL_POLL_MS = 60000;

// Countdown palette. Full class names on purpose - Tailwind only generates what it can
// see as a literal in the source.
const TONE = {
  prelive: { labelClass: 'text-[#ffb763]', valueClass: 'text-[#e9eef1]' },
  live: { labelClass: 'text-[#79e37a]', valueClass: 'text-[#e9eef1]' },
  over: { labelClass: 'text-[#ff8a8a]', valueClass: 'text-[#ff8a8a]' },
  idle: { labelClass: 'text-[#75838b]', valueClass: 'text-[#e9eef1]' },
};

const STATUS_COLORS = {
  ok: '#3ddc84',
  warn: '#ffb763',
  bad: '#ff5f5f',
  idle: '#8a979f',
  unknown: '#4d585f',
};

function debounce(fn, delay) {
  let timer = null;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

export default {
  name: 'SourceMonitoring',

  components: { HlsPlayer },

  data() {
    return {
      filters: { ...DEFAULT_FILTERS },
      // Drives the countdowns and the live/pre-live split, so a booking flips phase
      // on kick-off rather than on the next 30s events poll.
      nowMs: Date.now(),
      clockTimer: null,
      pollingTimer: null,
      liveAssignmentsPollingTimer: null,
      thumbnailPollingTimer: null,
      inputThumbnails: {},
      isFiltersDrawerOpen: false,
      selectedMatrixItem: null,
      // eventId -> expanded. Rows only open on click; an issue flags the row, it does not
      // open it - a screen full of self-opening cards is unreadable during an incident.
      expanded: {},
      // eventId -> which output the OUT card is showing
      outputTab: {},
      player: { visible: false, url: '', name: '' },
    };
  },

  computed: {
    ...mapState({
      inputs: (state) => state.sourceMonitoring.inputs,
      inputInfos: (state) => state.sourceMonitoring.inputInfos,
      outputs: (state) => state.sourceMonitoring.outputs,
      outputInfos: (state) => state.sourceMonitoring.outputInfos,
      monitoredEvents: (state) => state.sourceMonitoring.monitoredEvents,
      liveInputIds: (state) => state.sourceMonitoring.liveInputIds,
      liveOutputIds: (state) => state.sourceMonitoring.liveOutputIds,
      loading: (state) => state.sourceMonitoring.loading,
      loadingStatuses: (state) => state.sourceMonitoring.loadingStatuses,
      loadingLiveAssignments: (state) => state.sourceMonitoring.loadingLiveAssignments,
      eventsError: (state) => state.sourceMonitoring.eventsError,
      account: (state) => state.userAccount.account,
      clientAccounts: (state) => state.userAccount.clientAccounts,
      clients: (state) => state.userAccount.clients,
      isSysAdmin: (state) => state.userAccount.isSysAdmin,
    }),
    ...mapGetters('sourceMonitoring', [
      'isInputHealthy', 'getInputInfo', 'getInputState',
      'isOutputHealthy', 'getOutputInfo', 'getOutputState',
      'getInputEventId', 'getOutputEventId',
    ]),

    searchQuery: {
      get() { return this.filters.searchQuery; },
      set(value) { this.filters.searchQuery = value; },
    },

    showLive: {
      get() { return this.filters.showLive; },
      set(value) { this.filters.showLive = value; },
    },

    showPreLive: {
      get() { return this.filters.showPreLive; },
      set(value) { this.filters.showPreLive = value; },
    },

    issuesOnly: {
      get() { return this.filters.issuesOnly; },
      set(value) { this.filters.issuesOnly = value; },
    },

    allAccounts: {
      get() { return this.filters.allAccounts; },
      set(value) { this.filters.allAccounts = value; },
    },

    // Only worth offering to someone who can actually see more than their own account.
    canSeeAllAccounts() {
      if (this.isSysAdmin) return true;
      const accounts = Array.isArray(this.clientAccounts) ? this.clientAccounts.length : 0;
      return accounts > 1 || Object.keys(this.clients || {}).length > 1;
    },

    accountName() {
      return (this.account && this.account.name) || 'This account';
    },

    thumbnailRefreshLabel() {
      return `${THUMBNAIL_POLL_MS / 1000}s`;
    },

    currentCount() {
      return this.filteredEvents.length;
    },

    busy() {
      return this.loading || this.loadingStatuses || this.loadingLiveAssignments;
    },

    overlayActive() {
      return this.isFiltersDrawerOpen || Boolean(this.selectedMatrixItem);
    },

    mobileFiltersCount() {
      return Object.keys(DEFAULT_FILTERS)
        .filter((key) => this.filters[key] !== DEFAULT_FILTERS[key])
        .length;
    },

    mobileFiltersActive() {
      return this.mobileFiltersCount > 0;
    },

    // Phase re-derived from the ticking clock; the store stamps it once per fetch.
    phaseById() {
      const now = moment.utc(this.nowMs);
      return this.monitoredEvents.reduce((acc, event) => {
        acc[event.id] = monitoringStateOf(event, now);
        return acc;
      }, {});
    },

    // Account scope comes first so every count below speaks about what is on screen.
    scopedEvents() {
      const accountId = this.account && this.account.id;
      if (this.allAccounts || !this.canSeeAllAccounts || !accountId) return this.monitoredEvents;
      return this.monitoredEvents.filter((event) => event.account && event.account.id === accountId);
    },

    phaseCounts() {
      return this.scopedEvents.reduce((acc, event) => {
        const phase = this.phaseOf(event);
        acc[phase] = (acc[phase] || 0) + 1;
        return acc;
      }, { live: 0, 'pre-live': 0 });
    },

    // Phase/search/monitoring filtered, before the issues-only filter - polling and the
    // issue map hang off this so toggling "issues only" does not stop watching the rest.
    baseEvents() {
      let list = this.scopedEvents;

      const phases = [];
      if (this.showLive) phases.push('live');
      if (this.showPreLive) phases.push('pre-live');
      // Neither toggle on means no phase restriction rather than an empty screen.
      if (phases.length) {
        list = list.filter((event) => phases.includes(this.phaseOf(event)));
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter((event) => {
          const label = [
            event.name,
            event.fixture,
            event.sport,
            event.property,
            event.customer?.name,
            event.account?.name,
            event.inputProfile?.name,
            event.inputProfile?.provider?.name,
            this.eventInputName(event),
            ...this.eventOutputNames(event),
          ].filter(Boolean).join(' ').toLowerCase();
          return label.includes(q);
        });
      }

      return [...list].sort((left, right) => moment
        .utc(left.lineupTime || left.startTime)
        .diff(moment.utc(right.lineupTime || right.startTime)));
    },

    issuesByEvent() {
      return this.baseEvents.reduce((acc, event) => {
        acc[event.id] = this.collectIssues(event);
        return acc;
      }, {});
    },

    eventsWithIssuesCount() {
      return Object.values(this.issuesByEvent).filter((issues) => issues.length > 0).length;
    },

    filteredEvents() {
      if (!this.issuesOnly) return this.baseEvents;
      return this.baseEvents.filter((event) => (this.issuesByEvent[event.id] || []).length > 0);
    },

    // Only on-air events have runtime status worth polling; upcoming/past rows would
    // just multiply the request size for data that does not exist yet.
    activeEvents() {
      return this.baseEvents.filter((event) => ['live', 'pre-live'].includes(this.phaseOf(event)));
    },

    monitoredInputEntities() {
      const seen = new Set();
      return this.activeEvents.reduce((acc, event) => {
        const input = this.eventInput(event);
        if (input && !seen.has(input.id)) {
          seen.add(input.id);
          acc.push(input);
        }
        return acc;
      }, []);
    },

    monitoredInputIds() {
      return this.monitoredInputEntities.map((input) => input.id);
    },

    monitoredOutputIds() {
      const seen = new Set();
      this.activeEvents.forEach((event) => {
        this.eventOutputs(event).forEach((output) => seen.add(output.id));
      });
      return Array.from(seen);
    },

    // Watched instead of the arrays themselves: the clock hands those a fresh identity
    // every second, which would re-poll the statuses once a second.
    monitoredInputKey() {
      return this.monitoredInputIds.join(',');
    },

    monitoredOutputKey() {
      return this.monitoredOutputIds.join(',');
    },

    liveInputIdsSet() {
      return new Set(this.liveInputIds);
    },

    liveOutputIdsSet() {
      return new Set(this.liveOutputIds);
    },

    selectedMatrixEntity() {
      if (!this.selectedMatrixItem) return null;
      const collection = this.selectedMatrixItem.tab === 'inputs' ? this.inputs : this.outputs;
      return collection.find((item) => item.id === this.selectedMatrixItem.itemId) || null;
    },

    selectedMatrixInfo() {
      if (!this.selectedMatrixItem || !this.selectedMatrixEntity) return null;
      return this.selectedMatrixItem.tab === 'inputs'
        ? this.getInputInfo(this.selectedMatrixEntity.id)
        : this.getOutputInfo(this.selectedMatrixEntity.id);
    },
  },

  watch: {
    monitoredInputKey: 'handleMonitoredInputIdsChange',
    monitoredOutputKey() {
      this.debouncedFetchOutputInfos();
    },
    overlayActive: 'syncBodyScroll',
    filters: {
      handler: 'persistFilters',
      deep: true,
    },
  },

  created() {
    this.debouncedFetchInputInfos = debounce(function () {
      if (this.monitoredInputIds.length) this.fetchInputInfos(this.monitoredInputIds);
    }, 500);
    this.debouncedFetchOutputInfos = debounce(function () {
      if (this.monitoredOutputIds.length) this.fetchOutputInfos(this.monitoredOutputIds);
    }, 500);
    this.debouncedFetchThumbnails = debounce(function () {
      this.fetchVisibleInputThumbnails();
    }, 500);
    this.restoreFilters();
  },

  async mounted() {
    await Promise.all([this.fetchInputs(), this.fetchOutputs(), this.fetchLiveAssignments()]);
    await Promise.all([
      this.fetchInputInfos(this.monitoredInputIds),
      this.fetchOutputInfos(this.monitoredOutputIds),
      this.fetchVisibleInputThumbnails(),
    ]);
    this.startPolling();
    this.startLiveAssignmentsPolling();
    this.startThumbnailPolling();
    this.clockTimer = setInterval(() => { this.nowMs = Date.now(); }, 1000);
  },

  beforeDestroy() {
    clearInterval(this.clockTimer);
    this.stopPolling();
    this.stopLiveAssignmentsPolling();
    this.stopThumbnailPolling();
    this.closeFiltersDrawer();
    this.closeMatrixDetails();
    this.syncBodyScroll();
  },

  methods: {
    ...mapActions('sourceMonitoring', ['fetchInputs', 'fetchInputInfos', 'fetchOutputs', 'fetchOutputInfos', 'fetchLiveAssignments']),
    ...mapActions('routerAlt', ['fetchRouterThumbnails']),

    async refresh() {
      await Promise.all([this.fetchInputs(), this.fetchOutputs(), this.fetchLiveAssignments()]);
      await Promise.all([
        this.fetchInputInfos(this.monitoredInputIds),
        this.fetchOutputInfos(this.monitoredOutputIds),
        this.fetchVisibleInputThumbnails(),
      ]);
    },

    startPolling() {
      this.stopPolling();
      this.pollingTimer = setInterval(() => {
        this.fetchInputInfos(this.monitoredInputIds);
        this.fetchOutputInfos(this.monitoredOutputIds);
      }, STATUS_POLL_MS);
    },

    startLiveAssignmentsPolling() {
      this.stopLiveAssignmentsPolling();
      this.liveAssignmentsPollingTimer = setInterval(() => {
        this.fetchLiveAssignments();
      }, EVENTS_POLL_MS);
    },

    startThumbnailPolling() {
      this.stopThumbnailPolling();
      this.thumbnailPollingTimer = setInterval(() => {
        this.fetchVisibleInputThumbnails();
      }, THUMBNAIL_POLL_MS);
    },

    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },

    stopLiveAssignmentsPolling() {
      if (this.liveAssignmentsPollingTimer) {
        clearInterval(this.liveAssignmentsPollingTimer);
        this.liveAssignmentsPollingTimer = null;
      }
    },

    stopThumbnailPolling() {
      if (this.thumbnailPollingTimer) {
        clearInterval(this.thumbnailPollingTimer);
        this.thumbnailPollingTimer = null;
      }
    },

    handleMonitoredInputIdsChange() {
      this.debouncedFetchInputInfos();
      this.debouncedFetchThumbnails();
    },

    syncBodyScroll() {
      if (typeof document === 'undefined') return;
      document.body.style.overflow = this.overlayActive ? 'hidden' : '';
    },

    openFiltersDrawer() {
      this.isFiltersDrawerOpen = true;
    },

    closeFiltersDrawer() {
      this.isFiltersDrawerOpen = false;
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
    },

    persistFilters() {
      localStorage.setItem(OPS_MONITORING_FILTERS_KEY, JSON.stringify(this.filters));
    },

    restoreFilters() {
      const saved = localStorage.getItem(OPS_MONITORING_FILTERS_KEY);
      if (!saved) return;
      try {
        this.filters = { ...DEFAULT_FILTERS, ...JSON.parse(saved) };
      } catch (error) {
        this.filters = { ...DEFAULT_FILTERS };
      }
    },

    closeMatrixDetails() {
      this.selectedMatrixItem = null;
    },

    openInputDetails(event) {
      const input = this.eventInput(event);
      // Only inputs that exist in the monitoring inventory have status data to show.
      if (!input || !this.inputs.some((item) => item.id === input.id)) return;
      this.selectedMatrixItem = { tab: 'inputs', itemId: input.id };
    },

    openOutputDetails(output) {
      if (!this.outputs.some((item) => item.id === output.id)) return;
      this.selectedMatrixItem = { tab: 'outputs', itemId: output.id };
    },

    openPlayer(url, name) {
      this.player = { visible: true, url, name };
    },

    // The event carries only {id, name}; the full entity with type/probe ids lives in the inventory.
    eventInput(event) {
      const ref = event.inputOverride || event.input;
      if (!ref) return null;
      return this.inputs.find((input) => input.id === ref.id) || ref;
    },

    eventOutputs(event) {
      return (event.outputs?.items || [])
        .map((item) => item?.output)
        .filter(Boolean)
        .map((output) => this.outputs.find((item) => item.id === output.id) || output);
    },

    eventProvider(event) {
      return event.inputProfile?.provider || null;
    },

    eventHlsUrls(event, outputId) {
      return (event.actions?.items || [])
        .filter((action) => action?.assignedOutput?.id === outputId)
        .reduce((urls, action) => urls.concat(getTranscodeDestinationUrls(action)), []);
    },

    messageLevel(msg) {
      return messageSeverity(msg);
    },

    // Everything that should pull a row open: thumbnail failures, input/output messages
    // and unhealthy states reported without a message.
    collectIssues(event) {
      const issues = [];
      const input = this.eventInput(event);
      const phase = this.phaseOf(event);
      const onAir = ['live', 'pre-live'].includes(phase);

      if (input) {
        // A booking pointing at a retired or offline input never comes up, whatever the
        // runtime says - and the runtime usually says nothing at all for those.
        if (input.status === 'DELETED') {
          issues.push({ scope: 'Input', name: input.name, level: 'error', message: 'Input is deleted' });
        } else if (input.status === 'OFFLINE') {
          issues.push({ scope: 'Input', name: input.name, level: 'warning', message: 'Input is offline' });
        }

        const info = this.getInputInfo(input.id);
        // Only on air: an upcoming booking legitimately has no telemetry yet.
        if (phase === 'live' && !info) {
          issues.push({ scope: 'Input', name: input.name, level: 'warning', message: 'No status data from the source' });
        }

        // StreamDetails means MediaConnect actually reports sources - without it the
        // default "SRC 1" placeholder would flag every ROUTE input as disconnected.
        if (phase === 'live' && info?.StreamDetails && !this.isSourceConnected(input)) {
          issues.push({ scope: 'Input', name: input.name, level: 'error', message: 'No source connected' });
        }

        if (input.requiresPidInfo && !event.inputVideoPid && !event.inputAudioPid) {
          issues.push({ scope: 'Input', name: input.name, level: 'warning', message: 'PID info required but not set' });
        }

        const thumbnail = this.getThumbnailData(input);
        if (thumbnail.error) {
          issues.push({ scope: 'Input', name: input.name, level: 'error', message: thumbnail.error });
        } else if (thumbnail.warning) {
          issues.push({ scope: 'Input', name: input.name, level: 'warning', message: thumbnail.warning });
        }

        const messages = this.getInputMessages(input);
        messages
          .filter((msg) => this.messageLevel(msg) !== 'info')
          .forEach((msg) => issues.push({
            scope: 'Input',
            name: input.name,
            level: this.messageLevel(msg),
            message: msg.Message,
          }));

        if (this.isInputHealthy(input.id) === false && messages.length === 0) {
          issues.push({
            scope: 'Input',
            name: input.name,
            level: 'error',
            message: `Unhealthy (${this.getInputState(input.id) || 'unknown state'})`,
          });
        }
      } else if (onAir) {
        issues.push({
          scope: 'Input',
          name: event.inputProfile?.name || '-',
          level: 'error',
          message: event.inputProfile ? 'Profile has no input assigned' : 'No input assigned',
        });
      }

      const outputs = this.eventOutputs(event);
      if (outputs.length === 0 && onAir) {
        issues.push({ scope: 'Output', name: '-', level: 'warning', message: 'No outputs assigned' });
      }

      outputs.forEach((output) => {
        const name = output.displayName || output.name;

        if (output.status === 'DELETED') {
          issues.push({ scope: 'Output', name, level: 'error', message: 'Output is deleted' });
        } else if (output.status === 'OFFLINE') {
          issues.push({ scope: 'Output', name, level: 'warning', message: 'Output is offline' });
        }

        const sagaState = this.getOutputSagaState(output);
        if (typeof sagaState === 'string' && /fail|error/i.test(sagaState)) {
          issues.push({ scope: 'Output', name, level: 'error', message: `Provisioning ${sagaState}` });
        }

        const messages = this.getOutputMessages(output);
        messages
          .filter((msg) => this.messageLevel(msg) !== 'info')
          .forEach((msg) => issues.push({
            scope: 'Output',
            name,
            level: this.messageLevel(msg),
            message: msg.Message,
          }));

        if (this.isOutputHealthy(output.id) === false && messages.length === 0) {
          issues.push({
            scope: 'Output',
            name,
            level: 'error',
            message: `Unhealthy (${this.getOutputState(output.id) || 'unknown state'})`,
          });
        }
      });

      return issues;
    },

    issuesOf(event) {
      return this.issuesByEvent[event.id] || [];
    },

    // One card whatever the output count, so the row height never depends on how many
    // destinations a booking has; the OUT tag switches between them.
    visibleOutputs(event) {
      const output = this.eventOutputs(event)[this.outputIndexOf(event)];
      return output ? [output] : [];
    },

    // Numbers only make sense once there is something to switch to.
    switchableOutputs(event) {
      const outputs = this.eventOutputs(event);
      return outputs.length > 1 ? outputs : [];
    },

    outputIndexOf(event) {
      const index = this.outputTab[event.id] || 0;
      return index < this.eventOutputs(event).length ? index : 0;
    },

    selectOutput(event, index) {
      this.$set(this.outputTab, event.id, index);
    },

    statusSquareColor(input) {
      if (!this.getInputInfo(input.id)) return STATUS_COLORS.unknown;
      if (this.isInputHealthy(input.id) === false) return STATUS_COLORS.bad;
      return this.getInputState(input.id) === 'ACTIVE' ? STATUS_COLORS.ok : STATUS_COLORS.idle;
    },

    outputSquareColor(output) {
      if (!this.getOutputInfo(output.id)) return STATUS_COLORS.unknown;
      if (this.isOutputHealthy(output.id) === false) return STATUS_COLORS.bad;
      const state = this.getOutputState(output.id);
      if (state === 'ACTIVE' || state === 'RUNNING') return STATUS_COLORS.ok;
      if (state === 'STARTING' || state === 'RECOVERING') return STATUS_COLORS.warn;
      return STATUS_COLORS.idle;
    },

    isExpanded(event) {
      return this.expanded[event.id] === true;
    },

    toggleExpanded(event) {
      this.$set(this.expanded, event.id, !this.isExpanded(event));
    },

    async fetchVisibleInputThumbnails() {
      const visibleInputs = this.monitoredInputEntities;
      if (visibleInputs.length === 0) return;

      const probeInputs = visibleInputs.filter((input) => input.probeProjectId && input.probeTaskId);
      const routerInputs = visibleInputs.filter((input) => !(input.probeProjectId && input.probeTaskId));

      const inputIds = visibleInputs.map((input) => input.id);
      const timestamp = new Date();

      inputIds.forEach((inputId) => {
        const currentThumbnail = this.inputThumbnails[inputId];
        this.$set(this.inputThumbnails, inputId, {
          data: currentThumbnail?.data || null,
          timestamp: currentThumbnail?.timestamp || null,
          loading: true,
          error: null,
          warning: null,
        });
      });

      try {
        const fetchTasks = [];

        if (probeInputs.length > 0) {
          const probeTask = async () => {
            const projects = probeInputs.map((input) => ({
              projectId: input.probeProjectId,
              taskId: input.probeTaskId,
            }));
            const Servers = (await import('@/services/Servers')).default;
            const response = await Servers.getInputThumbs({ projects });
            const thumbsData = response?.data || {};

            probeInputs.forEach((input) => {
              const thumbnailData = thumbsData[input.probeProjectId];
              this.$set(this.inputThumbnails, input.id, {
                data: thumbnailData?.[input.probeTaskId]?.data || null,
                timestamp,
                loading: false,
                error: null,
                warning: null,
              });
            });
          };
          fetchTasks.push(probeTask());
        }

        if (routerInputs.length > 0) {
          const routerTask = async () => {
            const { thumbnails = {}, errors = [] } = await this.fetchRouterThumbnails({
              inputIds: routerInputs.map((input) => input.id),
              includeErrors: true,
            });
            const routerErrors = this.mapRouterThumbnailErrors(errors);

            routerInputs.forEach((input) => {
              const parsedThumbnailIssue = this.parseRouterThumbnailIssue(routerErrors[input.id]);
              this.$set(this.inputThumbnails, input.id, {
                data: thumbnails[input.id] || null,
                timestamp,
                loading: false,
                error: parsedThumbnailIssue && !parsedThumbnailIssue.warning
                  ? parsedThumbnailIssue.message
                  : null,
                warning: parsedThumbnailIssue?.warning ? parsedThumbnailIssue.message : null,
              });
            });
          };
          fetchTasks.push(routerTask());
        }

        await Promise.all(fetchTasks);
      } catch (error) {
        inputIds.forEach((inputId) => {
          this.$set(this.inputThumbnails, inputId, {
            data: null,
            timestamp,
            loading: false,
            error: 'Failed to fetch thumbnail',
            warning: null,
          });
        });
      }
    },
    mapRouterThumbnailErrors(errors) {
      if (!Array.isArray(errors)) return {};

      return errors.reduce((acc, entry) => {
        if (!entry?.inputId || !entry?.error) return acc;
        acc[entry.inputId] = entry.error;
        return acc;
      }, {});
    },

    parseRouterThumbnailIssue(errorMessage) {
      if (!errorMessage || typeof errorMessage !== 'string') return null;

      const cleanedMessage = errorMessage.trim();
      if (!cleanedMessage) return null;

      const isInputNotActive = /router input must be in the active state/i.test(cleanedMessage);
      if (isInputNotActive) {
        return {
          warning: true,
          message: cleanedMessage,
        };
      }

      return {
        warning: false,
        message: cleanedMessage,
      };
    },

    getThumbnailData(input) {
      const thumbnailData = this.inputThumbnails[input.id] || {
        data: null,
        loading: false,
        error: null,
        warning: null,
        timestamp: null,
      };
      let imgData = thumbnailData.data;
      if (typeof imgData !== 'string') {
        imgData = null;
      } else {
        imgData = imgData.trim();
        if (!imgData) {
          imgData = null;
        } else if (!imgData.startsWith('data:image')) {
          const isBase64 = /^[A-Za-z0-9+/=]+$/.test(imgData);
          imgData = isBase64 ? `data:image/jpeg;base64,${imgData}` : null;
        }
      }

      return {
        ...thumbnailData,
        data: imgData,
      };
    },

    thumbnailTooltip(input) {
      const thumbnailData = this.getThumbnailData(input);
      if (thumbnailData.warning) return `${input.name} • ${thumbnailData.warning}`;
      if (thumbnailData.error) return `${input.name} • ${thumbnailData.error}`;
      if (!thumbnailData.timestamp) return input.name;
      return `${input.name} • updated ${thumbnailData.timestamp.toLocaleTimeString()}`;
    },

    isInputLive(inputId) {
      return this.liveInputIdsSet.has(inputId);
    },

    isOutputLive(outputId) {
      return this.liveOutputIdsSet.has(outputId);
    },

    // Event id a monitored input/output belongs to (live events only).
    matrixEntityEventId(entity, tab) {
      if (!entity) return null;
      return tab === 'inputs'
        ? this.getInputEventId(entity.id)
        : this.getOutputEventId(entity.id);
    },

    // Deep-link to the resource's analytics report; resourceId + name let the
    // report page open straight to the matching incident.
    openResourceReport(entity, tab) {
      const eventId = this.matrixEntityEventId(entity, tab);
      if (!eventId) return;
      this.$router.push({
        name: 'eventanalyticsdetail',
        params: { eventId },
        query: { resourceId: entity.id, resourceName: entity.name },
      }).catch(() => {});
    },

    openEventReport(event) {
      this.$router.push({
        name: 'eventanalyticsdetail',
        params: { eventId: event.id },
      }).catch(() => {});
    },

    eventTitle(event) {
      return event.fixture || event.name || event.id;
    },

    eventSubtitle(event) {
      return [event.sport, event.property].filter(Boolean).join(' / ');
    },

    // Kick-off -> end, i.e. the two times the countdown above it points at.
    eventDateRangeText(event) {
      const start = moment.utc(event.startTime || null);
      const end = moment.utc(event.endTime || event.serviceEndTime || null);
      if (!start.isValid() || !end.isValid()) return '--';
      const endFormat = start.isSame(end, 'day') ? 'HH:mm' : 'D MMM HH:mm';
      return `${start.format('D MMM HH:mm')} → ${end.format(endFormat)}`;
    },

    // What the source was *contracted* to send: the booking's override wins over the
    // profile's own config, exactly as the ingest side resolves it.
    inputConnectionConfig(event) {
      const config = event.inputProfileOverride || parseInputProfileConfig(event.inputProfile?.config);
      return config && Object.keys(config).length ? config : null;
    },

    connectionSummary(config) {
      return (config.connection || [])
        .map((entry) => [entry.ip || 'any', entry.port].filter((part) => part || part === 0).join(':'))
        .filter((entry) => entry && entry !== 'any')
        .join(', ');
    },

    formatMbps(bitrate) {
      const value = Number(bitrate);
      if (!value) return null;
      return `${(value / 1000000).toFixed(2)} Mbps`;
    },

    // One glance at the collapsed row: how the source is supposed to reach us.
    // ponytail: addresses live in the tooltip/details only, the row shows protocol + mode.
    inputConnectionLabel(event) {
      const config = this.inputConnectionConfig(event);
      if (!config) return '';
      return [config.protocol, config.mode].filter(Boolean).join(' ');
    },

    inputConnectionTooltip(event) {
      const config = this.inputConnectionConfig(event);
      if (!config) return '';
      const params = config.parameters || {};
      return [
        this.inputConnectionLabel(event),
        this.connectionSummary(config),
        params.latency != null ? `latency ${params.latency} ms` : null,
        this.formatMbps(params.bitrate),
        params.routingScope,
        event.inputProfileOverride ? 'per-booking override' : null,
      ].filter(Boolean).join(' · ');
    },

    // Expanded panels: label/value rows, empties dropped so a panel never shows "-".
    inputDetailRows(event) {
      const input = this.eventInput(event);
      const profile = event.inputProfile;
      const config = this.inputConnectionConfig(event);
      // A profile with no input resolved yet still has everything ops needs to chase it.
      if (!input && !profile && !config) return [];
      const params = config?.parameters || {};

      return [
        { label: 'Profile', value: profile?.name },
        { label: 'Input', value: input?.name },
        { label: 'State', value: input && this.getInputState(input.id) },
        { label: 'Type', value: input?.type },
        { label: 'Mode', value: config && [config.protocol, config.mode].filter(Boolean).join(' / ') },
        { label: 'Source(s)', value: config && this.connectionSummary(config) },
        { label: 'Latency', value: params.latency != null ? `${params.latency} ms` : null },
        { label: 'Max Bitrate', value: this.formatMbps(params.bitrate) },
      ].filter((row) => row.value || row.value === 0);
    },

    providerRows(event) {
      const provider = this.eventProvider(event);
      return [
        { label: 'Contact', value: provider?.contactDetails },
        { label: 'Instructions', value: provider?.instructions },
        { label: 'Input details', value: event.inputDetails },
      ].filter((row) => row.value);
    },

    outputDetailRows(output) {
      return [
        { label: 'Channel', value: output.displayName || output.name },
        { label: 'Type', value: output.outputType },
        { label: 'Routed input', value: output.routedInput?.name },
      ].filter((row) => row.value);
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success({ message: 'Copied' });
      } catch (error) {
        this.$message.error({ message: 'Could not copy to clipboard' });
      }
    },

    // How stale the thumbnail is - the poll runs every 60s, a frozen number means trouble.
    thumbnailAge(input) {
      const { timestamp, data } = this.getThumbnailData(input);
      if (!timestamp || !data) return '';
      const seconds = Math.max(0, Math.round((this.nowMs - timestamp.getTime()) / 1000));
      return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m`;
    },

    eventInputName(event) {
      const input = event.inputOverride || event.input;
      return input ? (input.name || input.id || '') : '';
    },

    // A booking with a profile is contracted against the profile, not against whichever
    // input currently sits behind it - so the profile is the headline and the input the detail.
    eventInputLabel(event) {
      return event.inputProfile?.name || this.eventInputName(event);
    },

    eventInputSubLabel(event) {
      if (!event.inputProfile) return '';
      return this.eventInputName(event) || 'no input assigned';
    },

    eventOutputNames(event) {
      const names = (event.outputs?.items || [])
        .map((item) => item?.output && (item.output.displayName || item.output.name || item.output.id))
        .filter(Boolean);
      return Array.from(new Set(names));
    },

    phaseOf(event) {
      return this.phaseById[event.id] || event.monitoringState;
    },

    // Only the on-air phases get a phase badge; the rest falls back to the booking status.
    eventMonitoringStateClass(event) {
      if (this.phaseOf(event) === 'live') return 'border-[#3fd23f]/50 bg-[#3fd23f]/10 text-[#79e37a]';
      if (this.phaseOf(event) === 'pre-live') return 'border-[#f28c28]/50 bg-[#f28c28]/10 text-[#ffb763]';
      return '';
    },

    // Every phase gets the same shape - a label and a duration - so the card never
    // changes size as a booking moves through its timeline.
    // `|| null` throughout: moment.utc(undefined) is *now*, i.e. a countdown of zero.
    countdownOf(event) {
      const phase = this.phaseOf(event);

      if (phase === 'pre-live') {
        return this.countdownTo(TONE.prelive, 'starts in', event.startTime);
      }
      if (phase === 'live') {
        const end = moment.utc(event.endTime || event.serviceEndTime || null);
        const ms = end.valueOf() - this.nowMs;
        if (end.isValid() && ms < 0) return { ...TONE.over, label: 'overrun', ms: -ms };
        return this.countdownTo(TONE.live, 'ends in', event.endTime || event.serviceEndTime);
      }
      if (phase === 'upcoming') {
        return this.countdownTo(TONE.idle, 'lines up in', event.serviceStartTime || event.lineupTime);
      }
      const ended = moment.utc(event.endTime || event.serviceEndTime || null);
      return ended.isValid()
        ? { ...TONE.idle, label: 'ended', ms: this.nowMs - ended.valueOf() }
        : { ...TONE.idle, label: '--', ms: 0 };
    },

    countdownTo(tone, label, time) {
      const target = moment.utc(time || null);
      if (!target.isValid()) return { ...TONE.idle, label, ms: 0 };
      return { ...tone, label, ms: target.valueOf() - this.nowMs };
    },

    formatDuration(ms) {
      const total = Math.max(0, Math.floor(ms / 1000));
      const pad = (value) => String(value).padStart(2, '0');
      const days = Math.floor(total / 86400);
      const rest = `${pad(Math.floor((total % 86400) / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
      return days ? `${days}d ${rest}` : rest;
    },

    matrixInputSummary(input) {
      if (input.type === 'MEDIA_CONNECT') {
        const connectedIps = this.getSourceIps(input);
        return connectedIps.length > 0 ? connectedIps.join(', ') : 'No active source';
      }

      const info = this.getInputInfo(input.id);
      return info?.destination?.url || info?.source?.url || 'Tap to inspect connection';
    },

    matrixInputMeta(input) {
      const info = this.getInputInfo(input.id);
      if (input.type === 'MEDIA_CONNECT' && info?.RoutedOutputs != null) {
        return `${info.RoutedOutputs} outputs`;
      }
      if (info?.connectedClients != null) {
        return `${info.connectedClients} clients`;
      }
      return this.getInputState(input.id) || 'Unknown state';
    },

    matrixOutputSummary(output) {
      return this.getOutputDestinationIp(output) || this.getOutputDestination(output) || 'No active destination';
    },

    matrixOutputMeta(output) {
      if (output.routedInput?.name) {
        return `Input: ${output.routedInput.name}`;
      }
      return this.getOutputRoutedState(output) || this.getOutputState(output.id) || 'Tap to inspect connection';
    },

    getInputMessages(input) {
      const info = this.getInputInfo(input.id);
      if (!info || !info.Messages) return [];
      return info.Messages;
    },

    getProtocol(input) {
      const info = this.getInputInfo(input.id);
      const standardProtocol = info?.protocolConfiguration?.protocol
        || info?.protocolConfiguration?.Protocol
        || info?.Configuration?.Standard?.Protocol
        || info?.Configuration?.Protocol
        || input?.protocolConfiguration?.protocol
        || input?.protocolConfiguration?.Protocol
        || input?.mcInputProtocol
        || null;
      if (standardProtocol) {
        return String(standardProtocol).replace(/_/g, ' ');
      }

      const failoverConfigs = info?.Configuration?.Failover?.ProtocolConfigurations;
      if (!Array.isArray(failoverConfigs) || failoverConfigs.length === 0) return null;

      const protocols = failoverConfigs
        .map((config, index) => {
          const protocolKey = Object.keys(config || {})[0];
          if (!protocolKey) return null;
          const normalizedProtocol = protocolKey
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/_/g, ' ')
            .toUpperCase();
          return `SRC ${index + 1}: ${normalizedProtocol}`;
        })
        .filter(Boolean);

      if (protocols.length === 0) return null;
      return protocols.join(' / ');
    },

    extractAwsRegion(value) {
      if (typeof value !== 'string') return null;
      const match = value.match(
        /\b(us|eu|ap|sa|ca|af|me)-(north|south|east|west|central|northeast|northwest|southeast|southwest)-\d\b/i,
      );
      return match ? match[0].toLowerCase() : null;
    },

    extractRegionFromArn(arn) {
      if (typeof arn !== 'string') return null;
      const arnParts = arn.split(':');
      if (arnParts.length < 4) return null;
      return this.extractAwsRegion(arnParts[3]);
    },

    findAwsRegionInObject(value, depth = 0) {
      if (depth > 4 || value == null) return null;

      if (typeof value === 'string') {
        return this.extractAwsRegion(value);
      }

      if (Array.isArray(value)) {
        for (const item of value) {
          const region = this.findAwsRegionInObject(item, depth + 1);
          if (region) return region;
        }
        return null;
      }

      if (typeof value === 'object') {
        const explicitRegion = this.extractAwsRegion(
          value.AwsRegion || value.awsRegion || value.Region || value.region,
        );
        if (explicitRegion) return explicitRegion;

        const regionFromArn = this.extractRegionFromArn(
          value.Arn || value.arn || value.OutputArn || value.InputArn || value.FlowArn || value.SourceArn,
        );
        if (regionFromArn) return regionFromArn;

        for (const nested of Object.values(value)) {
          const region = this.findAwsRegionInObject(nested, depth + 1);
          if (region) return region;
        }
      }

      return null;
    },

    resolveAwsRegion(info, item) {
      const defaultRegion = 'eu-central-1';

      const regionFromInfo = this.findAwsRegionInObject(info);
      if (regionFromInfo) return regionFromInfo;

      const regionFromItem = this.findAwsRegionInObject(item);
      if (regionFromItem) return regionFromItem;

      if (item?.location) {
        try {
          const parsedLocation = JSON.parse(item.location);
          const regionFromLocation = this.findAwsRegionInObject(parsedLocation);
          if (regionFromLocation) return regionFromLocation;
        } catch {
          const regionFromLocationText = this.extractAwsRegion(item.location);
          if (regionFromLocationText) return regionFromLocationText;
        }
      }

      return defaultRegion;
    },

    getGrafanaUrl(inputName, inputInfo = null, input = null) {
      const awsRegion = this.resolveAwsRegion(inputInfo, input);
      return `https://g-931d259b22.grafana-workspace.eu-west-1.amazonaws.com/d/ffgbmgfjc8dtsd/media-connect-input-monitor?orgId=1&var-InputName=${encodeURIComponent(inputName)}&var-AwsRegion=${encodeURIComponent(awsRegion)}`;
    },

    getMediaConnectSources(input) {
      const info = this.getInputInfo(input.id);
      const streamDetails = info?.StreamDetails || {};
      const sources = [];

      const failover = streamDetails?.Failover;
      if (failover && typeof failover === 'object') {
        const failoverEntries = Object.entries(failover)
          .filter(([, details]) => details && typeof details === 'object' && Object.prototype.hasOwnProperty.call(details, 'SourceIndex'))
          .sort(([, left], [, right]) => {
            const leftIndex = Number.isFinite(left?.SourceIndex) ? left.SourceIndex : 0;
            const rightIndex = Number.isFinite(right?.SourceIndex) ? right.SourceIndex : 0;
            return leftIndex - rightIndex;
          });

        failoverEntries.forEach(([, details], position) => {
          const sourceIndex = Number.isFinite(details?.SourceIndex) ? details.SourceIndex : position;
          const ip = details?.SourceIpAddress || null;
          sources.push({
            index: sourceIndex,
            label: `SRC ${sourceIndex + 1}`,
            ip,
            connected: !!ip,
          });
        });
      }

      if (sources.length === 0) {
        const standardIp = streamDetails?.Standard?.SourceIpAddress || null;
        sources.push({ index: 0, label: 'SRC 1', ip: standardIp, connected: !!standardIp });
      }

      return sources;
    },

    getSourceIps(input) {
      return this.getMediaConnectSources(input)
        .filter((source) => source.connected)
        .map((source) => source.ip);
    },

    isSourceConnected(input) {
      return this.getMediaConnectSources(input).some((source) => source.connected);
    },

    connectionStatusLabel(input) {
      return this.isSourceConnected(input) ? 'Connected' : 'Not Connected';
    },

    connectionStatusClass(input) {
      return this.isSourceConnected(input)
        ? 'bg-green-900/20 text-green-400 border border-green-800/20'
        : 'bg-red-900/20 text-red-400 border border-red-800/20';
    },

    getSourceIp(input) {
      return this.getSourceIps(input)[0] || null;
    },

    parseLocation(location) {
      if (!location) return '';
      try {
        const parsed = JSON.parse(location);
        if (Array.isArray(parsed)) return parsed.join(', ');
        return String(parsed);
      } catch {
        return location;
      }
    },


    statusTooltip(input) {
      const info = this.getInputInfo(input.id);
      if (!info) return 'No status data';
      const healthy = this.isInputHealthy(input.id);
      if (healthy === false) return 'Has issues';
      const state = this.getInputState(input.id);
      if (state === 'ACTIVE') return 'Active';
      return state || 'Unknown';
    },

    statusLabel(input) {
      const healthy = this.isInputHealthy(input.id);
      if (healthy === null) return 'Unknown';
      return healthy ? 'Healthy' : 'Error';
    },

    statusLabelClass(input) {
      const healthy = this.isInputHealthy(input.id);
      if (healthy === null) return 'bg-slate-700/50 text-slate-400';
      return healthy
        ? 'bg-green-900/30 text-green-400'
        : 'bg-red-900/30 text-red-400';
    },

    inputTypeBadgeClass(type) {
      switch (type) {
        case 'MEDIA_CONNECT': return 'bg-orange-900/40 text-orange-400';
        case 'ROUTE': return 'bg-blue-900/40 text-blue-400';
        case 'TECHEX': return 'bg-[rgb(126,0,128)]/40 text-[rgb(200,150,201)]';
        default: return 'bg-slate-700/60 text-slate-400';
      }
    },

    messageClass(msg) {
      const level = this.messageLevel(msg);
      if (level === 'error') return 'text-red-400';
      if (level === 'warning') return 'text-yellow-400';
      return 'text-slate-400';
    },

    messageIcon(msg) {
      const level = this.messageLevel(msg);
      if (level === 'error') return '✕';
      if (level === 'warning') return '⚠';
      return 'ℹ';
    },

    // Output helpers
    getOutputMessages(output) {
      const info = this.getOutputInfo(output.id);
      if (!info || !info.Messages) return [];
      return info.Messages;
    },

    getOutputProtocol(output) {
      const info = this.getOutputInfo(output.id);
      if (!info?.Configuration?.Standard?.Protocol) return null;
      return info.Configuration.Standard.Protocol.replace(/_/g, ' ');
    },

    getOutputDestination(output) {
      const info = this.getOutputInfo(output.id);
      return info?.Destination?.Standard?.Url || null;
    },

    getOutputRoutedState(output) {
      const info = this.getOutputInfo(output.id);
      return info?.RoutedState || null;
    },

    getOutputDestinationIp(output) {
      const info = this.getOutputInfo(output.id);
      return info?.StreamDetails?.Standard?.DestinationIpAddress || null;
    },

    getOutputMediaLiveChannel(output) {
      const info = this.getOutputInfo(output.id);
      const channel = info?.additionalInfo?.mediaLiveChannel;
      if (!channel || !channel.State) return null;
      return channel;
    },

    getOutputSagaState(output) {
      const info = this.getOutputInfo(output.id);
      return info?.additionalInfo?.sagaState || null;
    },

    isMediaConnectOutputMediaLiveInput(output) {
      const info = this.getOutputInfo(output.id);
      return info?.additionalInfo?.mediaConnectOutput?.OutputType === 'MEDIALIVE_INPUT';
    },

    getGrafanaMediaLiveChannelUrl(output) {
      const channel = this.getOutputMediaLiveChannel(output);
      if (!channel || !channel.Id || !channel.Arn) return null;
      const arnParts = channel.Arn.split(':');
      const region = arnParts[3] || '';
      return `https://g-931d259b22.grafana-workspace.eu-west-1.amazonaws.com/d/efjqxvrtmku80a/media-live-resources?orgId=1&from=now-15m&to=now&var-Region=${encodeURIComponent(region)}&var-Channel=${encodeURIComponent(channel.Id)}`;
    },

    getGrafanaOutputUrl(outputName, outputInfo = null, output = null) {
      const awsRegion = this.resolveAwsRegion(outputInfo, output);
      return `https://g-931d259b22.grafana-workspace.eu-west-1.amazonaws.com/d/ffgbmgfjc8dtwrx/media-connect-output-monitor?orgId=1&refresh=5s&var-OutputName=${encodeURIComponent(outputName)}&var-AwsRegion=${encodeURIComponent(awsRegion)}`;
    },

    // The dashboards are keyed by the MediaConnect flow name, so no runtime info
    // (ROUTE inputs, outputs that are not up yet) means no link rather than a broken one.
    inputMetricsUrl(event) {
      const input = this.eventInput(event);
      const info = input && this.getInputInfo(input.id);
      return info?.Name ? this.getGrafanaUrl(info.Name, info, input) : null;
    },

    outputMetricsUrl(output) {
      const info = this.getOutputInfo(output.id);
      return info?.Name ? this.getGrafanaOutputUrl(info.Name, info, output) : null;
    },


    outputStatusTooltip(output) {
      const info = this.getOutputInfo(output.id);
      if (!info) return 'Output exists in DB, but runtime status is unavailable (Offline / Unknown)';
      const healthy = this.isOutputHealthy(output.id);
      if (healthy === false) return 'Has issues';
      const state = this.getOutputState(output.id);
      if (state === 'ACTIVE' || state === 'RUNNING') return 'Active';
      return state || 'Unknown';
    },

    outputStatusLabel(output) {
      const info = this.getOutputInfo(output.id);
      if (!info) return 'Offline / Unknown';
      const healthy = this.isOutputHealthy(output.id);
      if (healthy === null) return 'Unknown';
      return healthy ? 'Healthy' : 'Error';
    },

    outputStatusLabelClass(output) {
      const info = this.getOutputInfo(output.id);
      if (!info) return 'bg-red-900/30 text-red-300';
      const healthy = this.isOutputHealthy(output.id);
      if (healthy === null) return 'bg-slate-700/50 text-slate-400';
      return healthy
        ? 'bg-green-900/30 text-green-400'
        : 'bg-red-900/30 text-red-400';
    },
  },
};
</script>

<style scoped>
.source-monitoring-drawer-enter-active,
.source-monitoring-drawer-leave-active {
  transition: opacity 0.2s ease;
}

.source-monitoring-drawer-enter-active .absolute.right-0,
.source-monitoring-drawer-leave-active .absolute.right-0 {
  transition: transform 0.24s ease;
}

.source-monitoring-drawer-enter,
.source-monitoring-drawer-leave-to {
  opacity: 0;
}

.source-monitoring-drawer-enter .absolute.right-0,
.source-monitoring-drawer-leave-to .absolute.right-0 {
  transform: translateX(100%);
}

.source-monitoring-panel-enter-active,
.source-monitoring-panel-leave-active {
  transition: opacity 0.2s ease;
}

.source-monitoring-panel-enter-active .absolute.right-0,
.source-monitoring-panel-leave-active .absolute.right-0 {
  transition: transform 0.24s ease;
}

.source-monitoring-panel-enter,
.source-monitoring-panel-leave-to {
  opacity: 0;
}

.source-monitoring-panel-enter .absolute.right-0,
.source-monitoring-panel-leave-to .absolute.right-0 {
  transform: translateX(100%);
}
</style>
